<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import {
  listAuthUsers,
  setAuthDisabled,
  subscribeUserProfiles,
} from '../../../core/services/users.js'
import { useAuthStore } from '../../../app/store/auth.js'
import { formatDateTime, parseFirestoreTimestamp } from '@core/utils/formatting.js'
import {
  getUserRoleBadgeClass,
  getUserRoleLabel,
  getUserStatusBadgeClass,
  getUserStatusLabel,
} from '@core/utils/statusHelpers.js'
import { usePagination } from '@core/composables/usePagination.js'
import { useVersion } from '@core/composables/useVersion.js'
import UsersFiltersBar from '../../components/admin/users/UsersFiltersBar.vue'
import UserEditDrawer from '../../components/admin/users/UserEditDrawer.vue'
import UserCreateModal from '../../components/admin/users/UserCreateModal.vue'
import UserDeleteModal from '../../components/admin/users/UserDeleteModal.vue'
import ArchiveUsersModal from '../../components/admin/users/ArchiveUsersModal.vue'

// ============================================
// State
// ============================================
const authUsers = ref([])
const profilesByUid = ref({})
const isLoading = ref(true)
const error = ref(null)
const isFetchingAuthUsers = ref(false)

// Auth store & app version
const authStore = useAuthStore()
const { version: currentAppVersion } = useVersion()
const currentUserUid = computed(() => authStore.user?.authData?.uid)
const isSpecialAdmin = computed(() => currentUserUid.value === 'mmYOGqxs3yd5h1TuMoyT4nwIkwI3')

// Expand/collapse state
const expandedRows = ref(new Set())

// Sub-component open states
const drawerOpen = ref(false)
const drawerUser = ref(null)
const createModalOpen = ref(false)
const deleteModalOpen = ref(false)
const deleteModalUser = ref(null)
const archiveModalOpen = ref(false)

// Template refs
const filtersBarRef = ref(null)
const archiveModalRef = ref(null)

// ============================================
// Realtime subscription
// ============================================
let unsubscribeProfiles = null

onMounted(async () => {
  unsubscribeProfiles = subscribeUserProfiles((profiles, err) => {
    if (err) {
      console.error('Profiles subscription error:', err)
    } else {
      profilesByUid.value = profiles
    }
  })
  await fetchAuthUsers()
})

onUnmounted(() => {
  if (unsubscribeProfiles) unsubscribeProfiles()
})

// ============================================
// Fetch Auth Users
// ============================================
async function fetchAuthUsers() {
  isFetchingAuthUsers.value = true
  error.value = null
  try {
    const result = await listAuthUsers()
    authUsers.value = result.users || []
  } catch (err) {
    console.error('Failed to fetch auth users:', err)
    error.value = err.message || 'שגיאה בטעינת משתמשים'
  } finally {
    isFetchingAuthUsers.value = false
    isLoading.value = false
  }
}

// ============================================
// Merged Data
// ============================================
const mergedUsers = computed(() => {
  return authUsers.value.map((user) => ({
    ...user,
    profile: profilesByUid.value[user.uid] || null,
  }))
})

// ============================================
// Filtering
// ============================================
const filteredUsers = computed(() => {
  let result = mergedUsers.value

  const fb = filtersBarRef.value
  if (!fb) return result

  // Search filter
  const query = (fb.searchQuery || '').trim().toLowerCase()
  if (query) {
    result = result.filter((user) => {
      const name = (user.profile?.name || '').toLowerCase()
      const email = (user.email || '').toLowerCase()
      const phone = (user.profile?.phone || '').toLowerCase()
      const status = (user.profile?.status || '').toLowerCase()
      return (
        name.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        status.includes(query)
      )
    })
  }

  // Role filter
  const rf = fb.roleFilter || ''
  if (rf !== '') {
    if (rf === 'null') {
      result = result.filter((user) => user.role === null)
    } else {
      result = result.filter((user) => user.role === rf)
    }
  }

  // Client link filter
  const clf = fb.clientLinkFilter || ''
  if (clf === 'linked') {
    result = result.filter(
      (user) => user.profile?.client_makat && user.profile.client_makat.trim() !== ''
    )
  } else if (clf === 'not-linked') {
    result = result.filter(
      (user) => !user.profile?.client_makat || user.profile.client_makat.trim() === ''
    )
  }

  // Date filter (applied only)
  const adm = fb.appliedDateMode || ''
  const adf = fb.appliedDateFrom || ''
  const adt = fb.appliedDateTo || ''
  if (adm && (adf || adt)) {
    const startDate = adf ? new Date(adf + 'T00:00:00') : new Date(-8640000000000000)
    const endDate = adt ? new Date(adt + 'T23:59:59.999') : new Date(8640000000000000)

    result = result.filter((user) => {
      if (adm === 'signedIn') {
        const lastSignIn = user.metadata?.lastSignInTime
        if (!lastSignIn) return false
        const d = new Date(lastSignIn)
        return !isNaN(d.getTime()) && d >= startDate && d <= endDate
      } else if (adm === 'notSignedIn') {
        const lastSignIn = user.metadata?.lastSignInTime
        if (!lastSignIn) return true
        const d = new Date(lastSignIn)
        if (isNaN(d.getTime())) return true
        return d < startDate || d > endDate
      } else if (adm === 'joined') {
        const creation = user.metadata?.creationTime
        if (!creation) return false
        const d = new Date(creation)
        return !isNaN(d.getTime()) && d >= startDate && d <= endDate
      } else if (adm === 'active') {
        const lastActive = user.profile?.last_active_at
        if (!lastActive) return false
        const d = parseFirestoreTimestamp(lastActive)
        return d && !isNaN(d.getTime()) && d >= startDate && d <= endDate
      } else if (adm === 'notActive') {
        const lastActive = user.profile?.last_active_at
        if (!lastActive) return true
        const d = parseFirestoreTimestamp(lastActive)
        if (!d || isNaN(d.getTime())) return true
        return d < startDate || d > endDate
      }
      return true
    })
  }

  return result
})

// ============================================
// Pagination
// ============================================
const {
  currentPage,
  pageSize,
  totalPages,
  paginatedItems: pagedUsers,
  paginationInfo: paginationInfoData,
  visiblePages,
  pageSizeOptions,
  changePageSize,
  resetPagination,
} = usePagination(filteredUsers, {
  defaultPageSize: 10,
  pageSizeOptions: [10, 20, 50, 100],
})

const paginationInfo = computed(() => {
  const { start, end, total } = paginationInfoData.value
  if (total === 0) return 'אין משתמשים'
  return `${start}-${end} מתוך ${total}`
})

// ============================================
// Expand/Collapse
// ============================================
function toggleRow(uid) {
  if (expandedRows.value.has(uid)) {
    expandedRows.value.delete(uid)
  } else {
    expandedRows.value.add(uid)
  }
  expandedRows.value = new Set(expandedRows.value)
}

function isExpanded(uid) {
  return expandedRows.value.has(uid)
}

// ============================================
// Formatting Helpers
// ============================================
function formatValue(val) {
  if (val === null || val === undefined || val === '') return '—'
  return val
}

// ============================================
// Disable/Enable User (quick action)
// ============================================
async function toggleDisabled(user) {
  const newDisabled = !user.disabled
  try {
    await setAuthDisabled(user.uid, newDisabled)
    const idx = authUsers.value.findIndex((u) => u.uid === user.uid)
    if (idx !== -1) {
      authUsers.value[idx] = { ...authUsers.value[idx], disabled: newDisabled }
    }
  } catch (err) {
    console.error('Failed to toggle disabled:', err)
    alert('שגיאה: ' + (err.message || 'לא ניתן לעדכן סטטוס משתמש'))
  }
}

// ============================================
// Sub-component handlers
// ============================================
function openDrawer(user) {
  drawerUser.value = user
  drawerOpen.value = true
}

function closeDrawer() {
  drawerOpen.value = false
  drawerUser.value = null
}

function openCreateModal() {
  createModalOpen.value = true
}

function openDeleteModal(user) {
  deleteModalUser.value = user
  deleteModalOpen.value = true
}

function closeDeleteModal() {
  deleteModalOpen.value = false
  deleteModalUser.value = null
}

function handleDeleted(deletedUid) {
  // Also close drawer if open for this user
  if (drawerOpen.value && drawerUser.value?.uid === deletedUid) {
    closeDrawer()
  }
  fetchAuthUsers()
}

function handleDeleteFromDrawer(user) {
  openDeleteModal(user)
}

async function openArchiveModal() {
  archiveModalOpen.value = true
  await nextTick()
  if (archiveModalRef.value) {
    archiveModalRef.value.init()
  }
}
</script>

<template>
  <div class="admin-users">
    <!-- Page Header -->
    <header class="page-header">
      <div class="header-badge">
        <svg
          class="header-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
        <span>ניהול</span>
      </div>
      <h1 class="page-title">ניהול משתמשים</h1>
    </header>

    <!-- Actions Bar -->
    <div class="actions-bar">
      <button class="btn-primary" @click="openCreateModal">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>+ משתמש חדש</span>
      </button>
      <button v-if="isSpecialAdmin" class="btn-danger" @click="openArchiveModal">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="21 8 21 21 3 21 3 8" />
          <rect x="1" y="3" width="22" height="5" />
          <line x1="10" y1="12" x2="14" y2="12" />
        </svg>
        <span>מחק דוקים שהוסרו</span>
      </button>
      <button class="btn-secondary" :disabled="isFetchingAuthUsers" @click="fetchAuthUsers">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          :class="{ spin: isFetchingAuthUsers }"
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
        </svg>
        <span>{{ isFetchingAuthUsers ? 'מרענן...' : 'רענון' }}</span>
      </button>
      <button
        class="btn-filters-toggle"
        :class="{ active: filtersBarRef?.filtersExpanded }"
        @click="filtersBarRef?.toggleFilters()"
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        <span>סינון</span>
        <span v-if="filtersBarRef?.activeFiltersCount > 0" class="filters-count-badge">{{
          filtersBarRef.activeFiltersCount
        }}</span>
        <svg
          class="toggle-chevron"
          :class="{ expanded: filtersBarRef?.filtersExpanded }"
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <div class="results-badge">
        <span class="results-count">{{ filteredUsers.length }}</span>
      </div>
    </div>

    <!-- Filters Bar (extracted component) -->
    <UsersFiltersBar ref="filtersBarRef" @reset-pagination="resetPagination" />

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner">
        <svg class="spinner-ring" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" stroke-width="4" />
        </svg>
      </div>
      <p class="loading-text">טוען משתמשים...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon-wrapper">
        <svg
          viewBox="0 0 24 24"
          width="32"
          height="32"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </div>
      <p class="error-message">{{ error }}</p>
      <button class="btn-retry" @click="fetchAuthUsers">נסה שוב</button>
    </div>

    <!-- Content -->
    <div v-else class="content-section">
      <!-- Desktop Table -->
      <div class="table-container desktop-only">
        <table class="data-table">
          <thead>
            <tr>
              <th class="th-expand"></th>
              <th class="th-name">שם</th>
              <th class="th-email">אימייל</th>
              <th class="th-phone">טלפון</th>
              <th class="th-role">תפקיד</th>
              <th class="th-makat">מק״ט לקוח</th>
              <th class="th-status">סטטוס</th>
              <th class="th-version">גרסה</th>
              <th class="th-actions">פעולות</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="user in pagedUsers" :key="user.uid">
              <tr
                class="table-row"
                :class="{ 'row-expanded': isExpanded(user.uid), 'row-disabled': user.disabled }"
              >
                <td class="td-expand">
                  <button
                    class="expand-btn"
                    :class="{ expanded: isExpanded(user.uid) }"
                    @click="toggleRow(user.uid)"
                    aria-label="הרחב"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </td>
                <td class="td-name">
                  <div class="user-name-container">
                    <span class="cell-value">{{
                      formatValue(user.profile?.name || user.displayName)
                    }}</span>
                    <span v-if="user.profile?.client_name" class="client-name-subtitle">
                      {{ user.profile.client_name }}
                    </span>
                  </div>
                </td>
                <td class="td-email">
                  <span class="cell-value email-value">{{ formatValue(user.email) }}</span>
                </td>
                <td class="td-phone">
                  <span class="cell-value">{{
                    formatValue(user.profile?.phone || user.phoneNumber)
                  }}</span>
                </td>
                <td class="td-role">
                  <div class="role-badges">
                    <span class="role-badge" :class="getUserRoleBadgeClass(user.role)">
                      {{ getUserRoleLabel(user.role) }}
                    </span>
                    <span
                      v-if="user.role === 'agent' && user.profile?.agent"
                      class="agent-number-badge"
                    >
                      #{{ user.profile.agent }}
                    </span>
                  </div>
                </td>
                <td class="td-makat">
                  <span class="cell-value">{{ formatValue(user.profile?.client_makat) }}</span>
                </td>
                <td class="td-status">
                  <div class="status-cell">
                    <span v-if="user.disabled" class="disabled-badge">מושבת</span>
                    <span
                      v-else
                      class="status-badge"
                      :class="getUserStatusBadgeClass(user.profile?.status)"
                    >
                      {{ getUserStatusLabel(user.profile?.status) }}
                    </span>
                  </div>
                </td>
                <td class="td-version">
                  <span
                    v-if="user.profile?.last_app_version"
                    class="version-tag"
                    :class="
                      user.profile.last_app_version === currentAppVersion
                        ? 'version-current'
                        : 'version-outdated'
                    "
                  >
                    {{ user.profile.last_app_version }}
                  </span>
                  <span v-else class="cell-value">—</span>
                </td>
                <td class="td-actions">
                  <div class="actions-cell">
                    <button class="action-btn edit-btn" title="עריכה" @click="openDrawer(user)">
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      class="action-btn toggle-btn"
                      :class="{ 'is-disabled': user.disabled }"
                      :title="user.disabled ? 'הפעל' : 'השבת'"
                      @click="toggleDisabled(user)"
                    >
                      <svg
                        v-if="user.disabled"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0110 0v4" />
                      </svg>
                      <svg
                        v-else
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 019.9-1" />
                      </svg>
                    </button>
                    <button
                      class="action-btn delete-btn"
                      title="מחיקה"
                      @click="openDeleteModal(user)"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path
                          d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                        />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <!-- Expanded Row -->
              <tr v-if="isExpanded(user.uid)" class="expanded-row">
                <td colspan="9">
                  <div class="expanded-content">
                    <div class="detail-grid">
                      <div class="detail-item">
                        <span class="detail-label">UID</span>
                        <span class="detail-value uid-value">{{ user.uid }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">נוצר</span>
                        <span class="detail-value">{{
                          formatDateTime(user.metadata?.creationTime, { short: true })
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">התחברות אחרונה</span>
                        <span class="detail-value">{{
                          formatDateTime(user.metadata?.lastSignInTime, { short: true })
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">ספקי זיהוי</span>
                        <span class="detail-value">{{ user.providerIds?.join(', ') || '—' }}</span>
                      </div>
                    </div>
                    <div v-if="user.profile" class="profile-preview">
                      <span class="preview-label">פרופיל (JSON):</span>
                      <pre class="json-preview">{{ JSON.stringify(user.profile, null, 2) }}</pre>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="pagedUsers.length === 0">
              <td colspan="9" class="empty-cell">אין משתמשים להצגה</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Cards -->
      <div class="cards-container mobile-only">
        <div
          v-for="user in pagedUsers"
          :key="user.uid"
          class="user-card"
          :class="{ expanded: isExpanded(user.uid), disabled: user.disabled }"
        >
          <div class="card-header" @click="toggleRow(user.uid)">
            <div class="card-avatar">
              {{
                (user.profile?.name || user.displayName || user.email || '?')
                  .charAt(0)
                  .toUpperCase()
              }}
            </div>
            <div class="card-main">
              <span class="card-name">{{
                formatValue(user.profile?.name || user.displayName)
              }}</span>
              <span v-if="user.profile?.client_name" class="client-name-subtitle">
                {{ user.profile.client_name }}
              </span>
              <span class="card-email">{{ formatValue(user.email) }}</span>
            </div>
            <div class="card-badges">
              <span class="role-badge" :class="getUserRoleBadgeClass(user.role)">
                {{ getUserRoleLabel(user.role) }}
              </span>
              <span v-if="user.role === 'agent' && user.profile?.agent" class="agent-number-badge">
                #{{ user.profile.agent }}
              </span>
              <span v-if="user.disabled" class="disabled-badge">מושבת</span>
              <span
                v-else
                class="status-badge"
                :class="getUserStatusBadgeClass(user.profile?.status)"
              >
                {{ getUserStatusLabel(user.profile?.status) }}
              </span>
            </div>
            <svg
              class="card-chevron"
              :class="{ expanded: isExpanded(user.uid) }"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>

          <div v-if="isExpanded(user.uid)" class="card-details">
            <div class="card-row">
              <span class="card-label">טלפון</span>
              <span class="card-value">{{
                formatValue(user.profile?.phone || user.phoneNumber)
              }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">מק״ט לקוח</span>
              <span class="card-value">{{ formatValue(user.profile?.client_makat) }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">נוצר</span>
              <span class="card-value">{{
                formatDateTime(user.metadata?.creationTime, { short: true })
              }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">התחברות אחרונה</span>
              <span class="card-value">{{
                formatDateTime(user.metadata?.lastSignInTime, { short: true })
              }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">UID</span>
              <span class="card-value card-uid">{{ user.uid }}</span>
            </div>
            <div class="card-actions">
              <button class="card-action-btn edit-btn" @click.stop="openDrawer(user)">
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                <span>עריכה</span>
              </button>
              <button
                class="card-action-btn toggle-btn"
                :class="{ 'is-disabled': user.disabled }"
                @click.stop="toggleDisabled(user)"
              >
                <svg
                  v-if="user.disabled"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 019.9-1" />
                </svg>
                <span>{{ user.disabled ? 'הפעל' : 'השבת' }}</span>
              </button>
              <button class="card-action-btn delete-btn" @click.stop="openDeleteModal(user)">
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path
                    d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                  />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
                <span>מחיקה</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="pagedUsers.length === 0" class="empty-card">
          <p>אין משתמשים להצגה</p>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination-bar">
        <div class="page-size-selector">
          <span>הצג:</span>
          <select v-model="pageSize" class="page-size-select" @change="changePageSize(pageSize)">
            <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
          </select>
        </div>
        <div class="pagination-info">{{ paginationInfo }}</div>
        <div class="pagination-controls">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <template v-for="page in visiblePages" :key="page">
            <span v-if="page === '...'" class="page-ellipsis">...</span>
            <button
              v-else
              class="page-btn"
              :class="{ active: page === currentPage }"
              @click="currentPage = page"
            >
              {{ page }}
            </button>
          </template>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Sub-components -->
    <UserEditDrawer
      :open="drawerOpen"
      :user="drawerUser"
      :auth-users="authUsers"
      @close="closeDrawer"
      @updated="fetchAuthUsers"
      @open-delete="handleDeleteFromDrawer"
    />

    <UserCreateModal
      :open="createModalOpen"
      @close="createModalOpen = false"
      @created="fetchAuthUsers"
    />

    <UserDeleteModal
      :open="deleteModalOpen"
      :user="deleteModalUser"
      @close="closeDeleteModal"
      @deleted="handleDeleted"
    />

    <ArchiveUsersModal
      ref="archiveModalRef"
      :open="archiveModalOpen"
      @close="archiveModalOpen = false"
      @archived="fetchAuthUsers"
    />
  </div>
</template>

<style scoped>
/* ============================================
   Page Layout
   ============================================ */
.admin-users {
  padding: var(--spacing-md);
  max-width: 1400px;
  margin: 0 auto;
  height: calc(100vh - var(--header-height));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-header {
  margin-bottom: var(--spacing-md);
  flex-shrink: 0;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 4px 12px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: var(--border-radius-full);
  color: var(--color-primary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--spacing-sm);
}

.header-icon {
  width: 14px;
  height: 14px;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

/* ============================================
   Actions Bar
   ============================================ */
.actions-bar {
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  flex-wrap: wrap;
  flex-shrink: 0;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--gradient-primary);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-secondary:hover {
  background: var(--bg-secondary);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
}

.btn-danger:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   Filters Toggle Button
   ============================================ */
.btn-filters-toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-filters-toggle:hover {
  background: var(--bg-secondary);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.btn-filters-toggle.active {
  background: rgba(99, 102, 241, 0.1);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.filters-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--color-primary);
  border-radius: var(--border-radius-full);
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  color: white;
}

.toggle-chevron {
  transition: transform var(--transition-fast);
}

.toggle-chevron.expanded {
  transform: rotate(180deg);
}

.results-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  background: var(--gradient-primary);
  border-radius: var(--border-radius-full);
  margin-right: auto;
}

.results-count {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: white;
}

/* ============================================
   Loading & Error States
   ============================================ */
.loading-container,
.error-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-3xl);
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  margin-bottom: var(--spacing-md);
}

.spinner-ring {
  animation: spin 1s linear infinite;
}

.spinner-ring circle {
  stroke: var(--color-primary);
  stroke-dasharray: 80;
  stroke-dashoffset: 60;
  stroke-linecap: round;
}

.loading-text,
.error-message {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

.error-icon-wrapper {
  color: var(--color-error);
  margin-bottom: var(--spacing-md);
}

.btn-retry {
  margin-top: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
}

/* ============================================
   Table
   ============================================ */
.content-section {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.table-container {
  background: var(--bg-primary);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: var(--shadow-sm);
  flex: 1;
  min-height: 0;
}

.table-container::-webkit-scrollbar,
.cards-container::-webkit-scrollbar {
  width: 6px;
}

.table-container::-webkit-scrollbar-track,
.cards-container::-webkit-scrollbar-track {
  background: var(--bg-secondary);
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb,
.cards-container::-webkit-scrollbar-thumb {
  background: var(--color-gray-300);
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb:hover,
.cards-container::-webkit-scrollbar-thumb:hover {
  background: var(--color-gray-400);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.data-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

.data-table th {
  padding: var(--spacing-md);
  text-align: var(--text-align);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.data-table td {
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color-light);
  vertical-align: middle;
}

.table-row {
  transition: background var(--transition-fast);
}

.table-row:hover {
  background: var(--bg-secondary);
}

.table-row.row-expanded {
  background: rgba(99, 102, 241, 0.03);
}

.table-row.row-disabled {
  opacity: 0.6;
  background: rgba(239, 68, 68, 0.03);
}

.th-expand {
  width: 40px;
}
.th-name {
  min-width: 120px;
}
.th-email {
  min-width: 180px;
}
.th-phone {
  min-width: 120px;
}
.th-role {
  min-width: 80px;
}
.th-makat {
  min-width: 100px;
}
.th-status {
  min-width: 100px;
}
.th-version {
  min-width: 80px;
}
.version-tag {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
}
.version-current {
  color: var(--color-success);
  background: rgba(16, 185, 129, 0.1);
}
.version-outdated {
  color: var(--text-muted);
  background: var(--bg-tertiary);
}
.th-actions {
  min-width: 100px;
}

.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.expand-btn:hover {
  background: var(--color-primary);
  color: white;
}

.expand-btn svg {
  transition: transform var(--transition-fast);
}

.expand-btn.expanded svg {
  transform: rotate(180deg);
}

.cell-value {
  color: var(--text-primary);
}

.user-name-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.client-name-subtitle {
  font-size: 0.85em;
  color: #999;
  font-weight: normal;
}

.email-value {
  font-family: monospace;
  font-size: 12px;
}

.role-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
}

.role-admin {
  background: rgba(139, 92, 246, 0.15);
  color: #7c3aed;
}
.role-client {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
}
.role-agent {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}
.role-none {
  background: var(--bg-tertiary);
  color: var(--text-muted);
}

.role-badges {
  display: flex;
  align-items: center;
  gap: 6px;
}

.agent-number-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  font-family: monospace;
  background: rgba(16, 185, 129, 0.08);
  color: #047857;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.status-cell {
  display: flex;
  align-items: center;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
}

.status-new {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
}
.status-active {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}
.status-inactive {
  background: rgba(156, 163, 175, 0.2);
  color: #6b7280;
}
.status-none {
  background: var(--bg-tertiary);
  color: var(--text-muted);
}
.status-deleted {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.disabled-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.actions-cell {
  display: flex;
  gap: var(--spacing-xs);
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
  color: var(--text-secondary);
}

.action-btn:hover {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.action-btn.is-disabled {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: var(--color-error);
}

.action-btn.is-disabled:hover {
  background: var(--color-success);
  border-color: var(--color-success);
  color: white;
}

.action-btn.delete-btn {
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.3);
}

.action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #dc2626;
}

/* Expanded Row */
.expanded-row td {
  padding: 0;
  background: var(--bg-secondary);
}

.expanded-content {
  padding: var(--spacing-lg);
  border-top: 1px dashed var(--border-color);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  text-transform: uppercase;
}

.detail-value {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

.uid-value {
  font-family: monospace;
  font-size: 11px;
  word-break: break-all;
}

.profile-preview {
  margin-top: var(--spacing-md);
}

.preview-label {
  display: block;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  margin-bottom: var(--spacing-xs);
}

.json-preview {
  background: var(--bg-primary);
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  font-family: monospace;
  font-size: 11px;
  overflow-x: auto;
  max-height: 200px;
  border: 1px solid var(--border-color);
  text-align: left;
  direction: ltr;
}

.empty-cell {
  text-align: center;
  padding: var(--spacing-2xl) !important;
  color: var(--text-muted);
}

/* ============================================
   Pagination
   ============================================ */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) var(--spacing-md);
  margin-top: var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  flex-wrap: wrap;
  gap: var(--spacing-md);
  flex-shrink: 0;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

.page-size-select {
  padding: 6px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-secondary);
}

.pagination-info {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
  color: var(--text-secondary);
}

.page-btn:hover:not(:disabled) {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.page-btn.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-ellipsis {
  padding: 0 4px;
  color: var(--text-muted);
}

/* ============================================
   Mobile Cards
   ============================================ */
.cards-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  overflow-y: auto;
  padding: 2px;
  flex: 1;
  min-height: 0;
}

.user-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.user-card.expanded {
  border-color: var(--color-primary-light);
  box-shadow: var(--shadow-md);
}

.user-card.disabled {
  opacity: 0.7;
  background: var(--bg-secondary);
}

.card-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  cursor: pointer;
}

.card-avatar {
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-full);
  background: var(--gradient-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-base);
  flex-shrink: 0;
}

.card-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-email {
  font-size: 11px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}

.card-chevron {
  color: var(--text-muted);
  transition: transform var(--transition-fast);
  flex-shrink: 0;
}

.card-chevron.expanded {
  transform: rotate(180deg);
  color: var(--color-primary);
}

.card-details {
  padding: 0 var(--spacing-md) var(--spacing-md) var(--spacing-md);
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
  border-bottom: 1px solid var(--border-color-light);
}

.card-row:last-of-type {
  border-bottom: none;
}

.card-label {
  font-size: 12px;
  color: var(--text-secondary);
}

.card-value {
  font-size: 12px;
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
}

.card-uid {
  font-family: var(--font-family-mono);
  font-size: 10px;
  color: var(--text-muted);
}

.card-actions {
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-sm);
  border-top: 1px solid var(--border-color);
}

.card-action-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: 12px;
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.card-action-btn:hover {
  background: var(--bg-primary);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.card-action-btn.toggle-btn.is-disabled {
  border-color: var(--color-success);
  color: var(--color-success);
}

.card-action-btn.delete-btn {
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.3);
}

.card-action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #dc2626;
}

.empty-card {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-muted);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
}

/* ============================================
   Desktop/Mobile Visibility
   ============================================ */
.table-container.desktop-only {
  display: block;
}

.cards-container.mobile-only {
  display: none;
}

/* ============================================
   Responsive
   ============================================ */
@media (max-width: 768px) {
  .admin-users {
    padding: var(--spacing-sm);
    height: calc(100vh - 56px);
  }

  .page-header {
    margin-bottom: var(--spacing-sm);
  }

  .page-title {
    font-size: var(--font-size-lg);
  }

  .actions-bar {
    flex-wrap: wrap;
    gap: var(--spacing-xs);
    margin-bottom: var(--spacing-sm);
  }

  .btn-primary,
  .btn-secondary,
  .btn-danger,
  .btn-filters-toggle {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 12px;
  }

  .btn-primary span,
  .btn-secondary span,
  .btn-danger span {
    display: none;
  }

  .btn-filters-toggle span:first-of-type {
    display: inline;
  }

  .table-container.desktop-only {
    display: none;
  }

  .cards-container.mobile-only {
    display: flex;
  }
}
</style>
